/**
 * Client-side image optimization at upload time.
 *
 * Downscales raster images and re-encodes every JPEG/PNG/WebP as WebP (JPEG
 * fallback) using only browser APIs. Optimization is best-effort: any failure
 * returns the original file so uploads never break because of it.
 */

/** Mime types eligible for re-encoding. GIF (animation) and SVG (vector) pass through. */
export const OPTIMIZABLE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

/**
 * Files above this size are uploaded as-is: decoding them client-side risks
 * hanging the main thread or running out of memory.
 */
export const MAX_OPTIMIZABLE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

/** Maximum width/height of the optimized image. */
export const MAX_DIMENSION = 2000;

/** Lossy encoding quality for WebP/JPEG output. */
export const OUTPUT_QUALITY = 0.8;

/** Accepted mime types for image upload inputs (antd Upload `accept`). */
export const ACCEPTED_IMAGE_MIME_TYPES = "image/jpeg,image/png,image/webp";

/** Hard cap for image uploads validated in `beforeUpload`. */
export const MAX_UPLOAD_IMAGE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

/** Whether a file is worth processing based on its mime type and size. */
export const shouldOptimize = (mimeType: string, size: number): boolean =>
  (OPTIMIZABLE_MIME_TYPES as readonly string[]).includes(mimeType) &&
  size <= MAX_OPTIMIZABLE_SIZE_BYTES;

/**
 * Dimensions that fit within `maxDimension` preserving aspect ratio.
 * Never enlarges; never returns less than 1px per side.
 */
export const getTargetDimensions = (
  width: number,
  height: number,
  maxDimension: number = MAX_DIMENSION
): { width: number; height: number } => {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }
  const scale = Math.min(maxDimension / width, maxDimension / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/** Output file name: same base name, extension matching the output format. */
export const getOptimizedFileName = (
  originalName: string,
  outputType: string
): string => {
  const extension = outputType === "image/jpeg" ? ".jpg" : ".webp";
  const dotIndex = originalName.lastIndexOf(".");
  const baseName = dotIndex > 0 ? originalName.slice(0, dotIndex) : originalName;
  return `${baseName}${extension}`;
};

/** Load a file into an HTMLImageElement (fallback decode path). */
const loadImageElement = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to decode image"));
    };
    image.src = objectUrl;
  });

/**
 * Read the intrinsic dimensions of an image without materializing its full
 * bitmap: the `load` event only needs the metadata, pixel decoding is lazy.
 */
const getSourceDimensions = async (
  file: File
): Promise<{ width: number; height: number }> => {
  const image = await loadImageElement(file);
  return {
    width: image.naturalWidth || image.width,
    height: image.naturalHeight || image.height,
  };
};

/**
 * Decode a file into a bitmap already downscaled to the target size, so the
 * browser never materializes the full-resolution bitmap. Falls back to a
 * plain decode for browsers without resize options support.
 */
const createDownscaledBitmap = async (
  file: File,
  width: number,
  height: number
): Promise<ImageBitmap> => {
  try {
    return await createImageBitmap(file, {
      resizeWidth: width,
      resizeHeight: height,
      resizeQuality: "high",
    });
  } catch {
    // Older browsers may not support resize options.
    return createImageBitmap(file);
  }
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/**
 * Optimize an image file for upload: downscale to fit MAX_DIMENSION and
 * re-encode as WebP (JPEG fallback for JPEG sources when WebP is unavailable).
 * Every JPEG/PNG comes out as WebP; returns the original file only when the
 * type is not optimizable (GIF/SVG/non-image), the file exceeds the safety
 * cap, an already-WebP re-encode does not shrink it, or processing fails.
 */
export const optimizeImage = async (file: File): Promise<File> => {
  try {
    if (!shouldOptimize(file.type, file.size)) {
      return file;
    }

    let source: ImageBitmap | HTMLImageElement;
    let width: number;
    let height: number;

    if (typeof createImageBitmap === "function") {
      // Downscale during decode to avoid holding the full-size bitmap.
      const dimensions = await getSourceDimensions(file);
      ({ width, height } = getTargetDimensions(
        dimensions.width,
        dimensions.height
      ));
      source = await createDownscaledBitmap(file, width, height);
    } else {
      // Fallback: the image element only gets drawn onto the small canvas.
      const image = await loadImageElement(file);
      ({ width, height } = getTargetDimensions(
        image.naturalWidth || image.width,
        image.naturalHeight || image.height
      ));
      source = image;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }
    context.drawImage(source, 0, 0, width, height);
    if ("close" in source) {
      source.close();
    }

    let outputType = "image/webp";
    let blob = await canvasToBlob(canvas, outputType, OUTPUT_QUALITY);

    if (!blob || blob.type !== outputType) {
      // WebP encoding unavailable in this browser.
      if (file.type !== "image/jpeg") {
        // Keep the original for PNG (preserves transparency) and WebP sources.
        return file;
      }
      outputType = "image/jpeg";
      blob = await canvasToBlob(canvas, outputType, OUTPUT_QUALITY);
      if (!blob || blob.type !== outputType) {
        return file;
      }
    }

    // JPEG/PNG always come out as WebP (format normalization), even on the
    // rare image where the re-encode is not smaller. An already-WebP source
    // is only replaced when the re-encode actually shrinks it, and the JPEG
    // fallback (WebP encoder unavailable) only applies when it saves bytes.
    const convertsFormat =
      outputType === "image/webp" && file.type !== "image/webp";
    if (!convertsFormat && blob.size >= file.size) {
      return file;
    }

    return new File([blob], getOptimizedFileName(file.name, outputType), {
      type: outputType,
      lastModified: file.lastModified,
    });
  } catch (error) {
    console.warn("Image optimization failed, uploading original file:", error);
    return file;
  }
};
